import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Node, mergeAttributes } from '@tiptap/core';
import { Sigma, Bold, Italic, List } from 'lucide-react';
import 'mathlive';

// 1. Create the custom Tiptap Node for MathLive
const MathLiveExtension = Node.create({
  name: 'mathLive',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      value: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="math"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'math' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) => {
      return (
        <NodeViewWrapper as="span" className="inline-block align-middle px-1 mx-0.5 cursor-pointer rounded transition-colors group/math hover:bg-blue-50 dark:hover:bg-blue-900/30">
          {/* @ts-ignore */}
          <math-field 
            onInput={(e: any) => props.updateAttributes({ value: e.target.value })}
            style={{ 
              minWidth: '20px', 
              backgroundColor: 'transparent', 
              color: 'inherit',
              border: 'none', 
              outline: 'none',
              fontSize: '1.1em',
              padding: '2px 0'
            }}
          >
            {props.node.attrs.value}
          {/* @ts-ignore */}
          </math-field>
        </NodeViewWrapper>
      );
    });
  },
});

interface RichMathEditorProps {
  value: string; // The raw string containing $...$
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

// Helper: Convert raw string with $...$ to Tiptap JSON
const parseToTiptapJSON = (text: string) => {
  if (!text) return { type: 'doc', content: [{ type: 'paragraph' }] };
  
  const paragraphs = text.split('\n');
  const docContent = paragraphs.map(p => {
    const regex = /\$([\s\S]+?)\$/g;
    let match;
    let lastIndex = 0;
    const content = [];
    
    while ((match = regex.exec(p)) !== null) {
      if (match.index > lastIndex) {
        content.push({ type: 'text', text: p.substring(lastIndex, match.index) });
      }
      content.push({ type: 'mathLive', attrs: { value: match[1] } });
      lastIndex = regex.lastIndex;
    }
    
    if (lastIndex < p.length) {
      content.push({ type: 'text', text: p.substring(lastIndex) });
    }
    
    return { type: 'paragraph', content: content.length > 0 ? content : undefined };
  });

  return { type: 'doc', content: docContent };
};

// Helper: Convert Tiptap JSON back to raw string with $...$
const parseToRawString = (json: any) => {
  if (!json || !json.content) return '';
  return json.content.map((p: any) => {
    if (!p.content) return '';
    return p.content.map((node: any) => {
      if (node.type === 'text') return node.text;
      if (node.type === 'mathLive') return `$${node.attrs.value}$`;
      return '';
    }).join('');
  }).join('\n');
};

export function RichMathEditor({ value, onChange, placeholder, className = '' }: RichMathEditorProps) {
  const isUpdatingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      MathLiveExtension,
    ],
    content: parseToTiptapJSON(value),
    editorProps: {
      attributes: {
        class: 'prose dark:prose-invert max-w-none focus:outline-none min-h-[40px] px-5 py-3 text-sm text-slate-800 dark:text-slate-200 leading-relaxed',
        placeholder: placeholder || 'Type text here...'
      },
    },
    onUpdate: ({ editor }) => {
      isUpdatingRef.current = true;
      const json = editor.getJSON();
      const rawString = parseToRawString(json);
      onChange(rawString);
      setTimeout(() => { isUpdatingRef.current = false; }, 50);
    },
  });

  // Sync external changes
  useEffect(() => {
    if (editor && !isUpdatingRef.current) {
      const currentRaw = parseToRawString(editor.getJSON());
      if (currentRaw !== value) {
        editor.commands.setContent(parseToTiptapJSON(value));
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  const insertMath = () => {
    editor.chain().focus().insertContent({ type: 'mathLive', attrs: { value: '' } }).run();
  };

  return (
    <div className={`w-full group/editor flex flex-col ${className}`}>
      
      {/* Editor Content Area */}
      <div className="flex-1 cursor-text" onClick={() => editor.commands.focus()}>
        <EditorContent editor={editor} />
      </div>

      {/* Sleek Toolbar (Appears on Hover/Focus) */}
      <div className="opacity-0 group-focus-within/editor:opacity-100 group-hover/editor:opacity-100 transition-opacity flex items-center gap-1 mt-2">
        <button 
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${editor.isActive('bold') ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white' : ''}`}
          title="Bold"
        >
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button 
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${editor.isActive('italic') ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white' : ''}`}
          title="Italic"
        >
          <Italic className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-1"></div>
        <button 
          onClick={insertMath}
          className="flex items-center gap-1 px-1.5 py-1 rounded text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs font-bold transition-colors"
          title="Insert Math Equation"
        >
          <Sigma className="w-3.5 h-3.5" />
          <span>Add Math</span>
        </button>
      </div>
    </div>
  );
}
